import React, { useEffect, useState } from 'react';
import { App, Button, Dropdown, Empty, Table, Tag, Tooltip, Typography } from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import { AppstoreAddOutlined, DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../utils/feedback';
import {
  ProductCategory,
  ProductCategoryAssignedProduct,
  categoryErrorMessage,
  deleteProductCategoryTag,
  fetchProductCategoryAssignmentCandidates,
  fetchProductCategoryTags,
} from '../../modules/products/productCategoriesApi';
import CategoryTag from '../../modules/products/category/CategoryTag';
import './ProductCategoriesTab.css';

const { Text } = Typography;

const PRODUCT_PREVIEW_NAME_MAX_LENGTH = 36;

interface ProductCategoriesTabProps {
  canEdit: boolean;
  /** Called after a delete so the parent can refresh selects/filters. */
  onCategoriesChanged?: () => void;
}

function getProductPreviewName(product: ProductCategoryAssignedProduct): string {
  const name = product.name.trim();
  if (name.length <= PRODUCT_PREVIEW_NAME_MAX_LENGTH) return name;
  return `${name.slice(0, PRODUCT_PREVIEW_NAME_MAX_LENGTH - 1).trimEnd()}…`;
}

const categoryPath = (id: string) => `/app/products/categories/${id}`;

/**
 * Kategorie tab: list only. Create, edit, product assignment and delete live
 * on the category card (pages/ProductCategoryDetail.tsx); the row `⋯` menu is
 * the first column (DESIGN.md, Tables) so it never falls off a narrow screen.
 */
const ProductCategoriesTab: React.FC<ProductCategoriesTabProps> = ({ canEdit, onCategoriesChanged }) => {
  const { t } = useTranslation(['products', 'common']);
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [products, setProducts] = useState<ProductCategoryAssignedProduct[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [loadedCategories, loadedProducts] = await Promise.all([
        fetchProductCategoryTags(),
        fetchProductCategoryAssignmentCandidates(),
      ]);
      setCategories(loadedCategories);
      setProducts(loadedProducts);
    } catch (error) {
      feedback.error(t('products:productCategory.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = (category: ProductCategory) => {
    const productCount = category.productCount ?? 0;
    modal.confirm({
      title: t('products:productCategory.deleteConfirmTitle'),
      content: productCount > 0
        ? t('products:productCategory.deleteConfirmWithProducts', { count: productCount })
        : t('products:productCategory.deleteConfirm'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteProductCategoryTag(category.id);
          feedback.success(t('products:productCategory.deletedSuccess', { name: category.name }));
          await load();
          onCategoriesChanged?.();
        } catch (error: any) {
          feedback.error(categoryErrorMessage(error, t, t('products:productCategory.deleteFailed', { name: category.name })));
        }
      },
    });
  };

  const rowMenu: MenuProps['items'] = [
    { key: 'open', icon: <EditOutlined />, label: t('products:productCategory.openCard') },
    ...(canEdit ? [
      { key: 'assign', icon: <AppstoreAddOutlined />, label: t('products:productCategory.assignProducts') },
      { type: 'divider' as const },
      { key: 'delete', danger: true, icon: <DeleteOutlined />, label: t('common:delete') },
    ] : []),
  ];

  const handleRowAction = (key: string, category: ProductCategory) => {
    if (key === 'open') navigate(categoryPath(category.id));
    if (key === 'assign') navigate(`${categoryPath(category.id)}#products`);
    if (key === 'delete') handleDelete(category);
  };

  const renderProductPreview = (categoryId: string) => {
    const assignedProducts = products.filter((product) => product.productCategoryId === categoryId);
    if (assignedProducts.length === 0) {
      return <Text type="secondary">{t('products:productCategory.noAssignedProducts')}</Text>;
    }
    const visibleProducts = assignedProducts.slice(0, 3);
    return (
      <div className="product-categories-preview">
        {visibleProducts.map((product) => (
          <Tooltip key={product.id} title={product.name}>
            <Tag>{getProductPreviewName(product)}</Tag>
          </Tooltip>
        ))}
        {assignedProducts.length > visibleProducts.length && (
          <Text type="secondary">
            {t('products:productCategory.productsPreviewMore', {
              count: assignedProducts.length - visibleProducts.length,
            })}
          </Text>
        )}
      </div>
    );
  };

  const columns: TableColumnsType<ProductCategory> = [
    {
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_value, record) => (
        <Dropdown
          menu={{ items: rowMenu, onClick: ({ key }) => handleRowAction(key, record) }}
          trigger={['click']}
        >
          <Button
            size="small"
            icon={<MoreOutlined />}
            title={t('common:actions')}
            aria-label={t('common:actions')}
          />
        </Dropdown>
      ),
    },
    {
      title: t('products:productCategory.category'),
      key: 'name',
      width: 300,
      render: (_value, record) => (
        <div className="product-categories-identity">
          <button type="button" className="product-categories-name" onClick={() => navigate(categoryPath(record.id))}>
            <CategoryTag categoryId={record.id} name={record.name} color={record.color} />
          </button>
          {record.description && <small>{record.description}</small>}
        </div>
      ),
    },
    {
      title: t('products:productCategory.dimensionDisplayProfile'),
      dataIndex: 'dimensionDisplayProfile',
      key: 'dimensionDisplayProfile',
      width: 240,
      render: (profile: string | null) => (
        <Text className="product-categories-profile">
          {t(`products:productCategory.dimensionProfiles.${profile || 'GENERIC'}`)}
        </Text>
      ),
    },
    {
      title: t('products:productCategory.productCount'),
      key: 'productCount',
      width: 130,
      align: 'right',
      render: (_value, record) => record.productCount ?? 0,
    },
    {
      title: t('products:productCategory.assignedProducts'),
      key: 'assignedProducts',
      render: (_value, record) => renderProductPreview(record.id),
    },
  ];

  return (
    <div>
      <div className="app-page__card-intro">
        <Text type="secondary">{t('products:productCategory.tabHint')}</Text>
        {canEdit && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(categoryPath('new'))}>
            {t('products:productCategory.add')}
          </Button>
        )}
      </div>

      <Table<ProductCategory>
        className="product-categories-table"
        columns={columns}
        dataSource={categories}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        scroll={{ x: 1000 }}
        loading={loading}
        pagination={false}
        locale={{ emptyText: <Empty description={t('products:productCategory.empty')} /> }}
      />
    </div>
  );
};

export default ProductCategoriesTab;
